// Locale → pricing copy, following the site's language (i18next, set by the language switcher or
// the browser). Like the legal pages (src/content/legal), the copy is typed per-locale data rather
// than i18n JSON, so long structured copy stays readable and TypeScript checks every translation
// has the same shape as English.
//
// Translated: English (source of truth), Spanish and Portuguese. Every other site language falls
// back to English until it's translated.

import { useTranslation } from "react-i18next";
import en, { type PricingCopy } from "./copy.en";
import es from "./copy.es";
import pt from "./copy.pt";

const COPY_BY_LANG: Record<string, PricingCopy> = { en, es, pt };

/** Number formatting for each translated language (digit grouping and decimal mark). */
const NUMBER_LOCALE: Record<string, string> = { en: "en-GB", es: "es-ES", pt: "pt-BR" };

const baseLang = (lang: string | undefined) => (lang || "en").toLowerCase().split("-")[0];

export const getPricingCopy = (lang: string | undefined): PricingCopy => COPY_BY_LANG[baseLang(lang)] ?? en;

export interface PricingLocale {
  copy: PricingCopy;
  /** The copy's language ("en" for any untranslated site language). */
  lang: string;
  /** Intl locale for numbers, e.g. "es-ES". */
  numberLocale: string;
}

export function usePricingCopy(): PricingLocale {
  const { i18n } = useTranslation();
  const base = baseLang(i18n.language);
  const lang = COPY_BY_LANG[base] ? base : "en";
  return { copy: COPY_BY_LANG[lang], lang, numberLocale: NUMBER_LOCALE[lang] };
}

/** Replace "{name}" placeholders. Unknown names are left as they are. */
export const fill = (s: string, vars: Record<string, string | number>) =>
  s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));

/** 1000 → "1,000" (en) / "1000" (es) / "1.000" (pt). */
export const formatCount = (n: number, numberLocale: string) => new Intl.NumberFormat(numberLocale).format(n);

/** US$ plan prices: 29 → "$29", 16.99 → "$16.99" ("$16,99" in es/pt). */
export const formatUsd = (n: number, numberLocale: string) =>
  `$${new Intl.NumberFormat(numberLocale, {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n)}`;

/** US cents for small per-photo prices: 0.0135 → "1.35¢". */
export const formatCents = (usd: number, numberLocale: string) =>
  `${new Intl.NumberFormat(numberLocale, { maximumFractionDigits: 2 }).format(Math.round(usd * 10000) / 100)}¢`;

/** Storage in GB → "50 GB", "1 TB", "1.4 TB" (1 TB = 1,000 GB). */
export function formatStorage(gb: number, copy: PricingCopy, numberLocale: string): string {
  const nf = new Intl.NumberFormat(numberLocale, { maximumFractionDigits: 1 });
  return gb >= 1000 ? fill(copy.common.tb, { n: nf.format(gb / 1000) }) : fill(copy.common.gb, { n: nf.format(gb) });
}

export type { PricingCopy };
